import { createGtm } from "@gtm-support/vue-gtm";
import * as Sentry from "@sentry/vue";
import { createPinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import Toast, { POSITION, TYPE } from "vue-toastification";
import "vue-toastification/dist/index.css";
import { parse, stringify } from "zipson";

import "./style.css";
import { getCurrentUser } from "@/firebase";
import { validateUserRole } from "@/services/auth.service";
import { getUserDetails } from "@/services/user.service";
import { useUserStore } from "@/stores/user.store";
import $toast from "@/utils/toast";

import App from "./App.vue";
import router from "./router";

// Initialize app
const initializeApp = async () => {
	// Create the Vue app FIRST
	const app = createApp(App);

	// Initialize Sentry
	Sentry.init({
		app,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		integrations: [
			Sentry.browserTracingIntegration({ router }),
			Sentry.browserProfilingIntegration(),
		],
		tracesSampleRate: 0.1,
		profilesSampleRate: 1.0, // Profile 100% of the transactions. This value is relative to tracesSampleRate
		beforeSend(event) {
			if (event.level === "info") {
				return null;
			}
			return event;
		},
	});

	// Create the Pinia store
	const store = createPinia();
	store.use(
		createPersistedState({
			debug: true,
			serializer: {
				deserialize: parse,
				serialize: stringify,
			},
		}),
	);
	app.use(store);

	// Configure Toast
	app.use(Toast, {
		maxToasts: 5,
		newestOnTop: true,
		position: POSITION.TOP_CENTER,
		hideProgressBar: true,
		closeButton: false,
		timeout: 3000,
	});

	// Configure GTM
	app.use(
		createGtm({
			id: import.meta.env.VITE_GTM_ID,
			enabled: true,
			debug: import.meta.env.DEV,
			defer: true,
			vueRouter: router,
			loadScript: true,
		}),
	);

	// Check if user is authenticated
	// After redirect from Google, onAuthStateChanged automatically fires
	// and getCurrentUser will return the authenticated user
	try {
		const user = await getCurrentUser();

		if (user) {
			console.log("✅ User found via auth state:", {
				uid: user.uid,
				email: user.email,
				displayName: user.displayName,
			});

			// Validate that user has driver role
			await validateUserRole(user);

			// Set user in store and fetch details
			const userStore = useUserStore();
			userStore.setUser(user);
			await getUserDetails(user.uid);

			console.log("✅ User successfully authenticated and validated!");
		} else {
			console.log("ℹ️ No authenticated user found");
		}
	} catch (error) {
		console.error("❌ Error during authentication:", error);
		$toast((error as Error).message, TYPE.ERROR);
	}

	// Mount the app
	app.use(router);
	app.mount("#app");
};

// Start the app
initializeApp();
